/**
 * Tax Position Centre — dashboard (Phase 1).
 *
 * Replaces the dashboard UX from the legacy Tax Liability feature. 4
 * summary cards (overdue / due soon / up-to-date / IRD check due) plus
 * a client table matching the brief's mock-up. Each row links to the
 * four-step staff workspace.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Clock, CheckCircle2, Search, RefreshCw, MoreVertical } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";

const nz = (v) => (v == null ? "—" : `NZ$${Number(v).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`);

const STATUS_PILL = {
    overdue:  { label: "Overdue",  cls: "border-red-200 bg-red-50 text-red-700" },
    due_soon: { label: "Due Soon", cls: "border-amber-200 bg-amber-50 text-amber-700" },
    current:  { label: "Current",  cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
    unknown:  { label: "Unknown",  cls: "border-zinc-200 bg-zinc-50 text-zinc-600" },
};

export default function TaxPosition() {
    const [data, setData] = useState({ rows: [], cards: {}, settings: {} });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");
    const [seeding, setSeeding] = useState(false);

    async function load() {
        try {
            setLoading(true);
            const { data } = await api.get("/tax-position/dashboard");
            setData(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load Tax Position dashboard");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    async function seedDemo() {
        try {
            setSeeding(true);
            const { data } = await api.post("/tax-position/demo/seed");
            toast.success(`Seeded ${data.inserted} demo client(s)`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Seed failed");
        } finally { setSeeding(false); }
    }

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (data.rows || []).filter((r) => {
            if (filter === "overdue" && r.status !== "overdue") return false;
            if (filter === "due_soon" && r.status !== "due_soon") return false;
            if (filter === "current" && (r.status !== "current" || r.has_unknown)) return false;
            if (filter === "ird_check" && !(r.stale || r.has_unknown)) return false;
            if (q && !(r.client_name || "").toLowerCase().includes(q) &&
                !(r.ird_number || "").toLowerCase().includes(q)) return false;
            return true;
        });
    }, [data.rows, search, filter]);

    const c = data.cards || {};
    const cards = [
        { key: "overdue",   num: c.overdue   || 0, label: "Clients overdue",     action: "Need attention",      icon: AlertCircle,   wrap: "bg-red-500",     ring: "ring-red-500/20" },
        { key: "due_soon",  num: c.due_soon  || 0, label: "Clients due soon",    action: "Check and update",   icon: Clock,          wrap: "bg-amber-500",   ring: "ring-amber-500/20" },
        { key: "current",   num: c.current   || 0, label: "Clients up to date",  action: "No action required",  icon: CheckCircle2,   wrap: "bg-emerald-500", ring: "ring-emerald-500/20" },
        { key: "ird_check", num: c.ird_check_due || 0, label: "IRD check due",   action: "Not checked recently", icon: Clock,          wrap: "bg-zinc-500",    ring: "ring-zinc-500/20" },
    ];

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="tax-position-page">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-[22px] font-bold text-[#0F1115] tracking-tight">Tax Position Centre</h1>
                    <p className="text-[13px] text-[#5E636E] mt-0.5">Monitor your clients' IRD position and help them stay on track.</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5E636E]" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search clients..." className="h-9 pl-8 w-56 text-[13px]"
                            data-testid="tax-position-search" />
                    </div>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="h-9 w-40 text-[13px]" data-testid="tax-position-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All clients</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                            <SelectItem value="due_soon">Due soon</SelectItem>
                            <SelectItem value="current">Up to date</SelectItem>
                            <SelectItem value="ird_check">IRD check due</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" className="h-9 gap-1.5 text-[12.5px]" onClick={load}
                        disabled={loading} data-testid="refresh-btn">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                        Update from myIR
                    </Button>
                </div>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {cards.map((card) => {
                    const Icon = card.icon;
                    const active = filter === card.key;
                    return (
                        <button key={card.key} onClick={() => setFilter(active ? "all" : card.key)}
                            className={`text-left rounded-xl bg-white border border-[#E4E4E7] p-4 hover:ring-2 ${card.ring} transition ${active ? `ring-2 ${card.ring}` : ""}`}
                            data-testid={`card-${card.key}`}>
                            <div className="flex items-center gap-3">
                                <div className={`h-10 w-10 rounded-full ${card.wrap} text-white flex items-center justify-center`}>
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-[#0F1115] leading-none tabular-nums">{card.num}</div>
                                    <div className="text-[12px] text-[#5E636E] mt-1">{card.label}</div>
                                </div>
                            </div>
                            <div className="text-[11.5px] text-[#5E636E] mt-2 underline underline-offset-2">{card.action}</div>
                        </button>
                    );
                })}
            </div>

            <section className="rounded-xl border border-[#E4E4E7] bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead className="bg-[#FAFAFA]">
                            <tr className="text-left text-[11px] uppercase tracking-wider text-[#5E636E]">
                                <th className="px-3 py-3 w-6"></th>
                                <th className="px-3 py-3">Client</th>
                                <th className="px-3 py-3">Last IRD Check</th>
                                <th className="px-3 py-3 text-right">Overdue Now</th>
                                <th className="px-3 py-3 text-right">Due Soon (≤ 30 days)</th>
                                <th className="px-3 py-3 text-right">Future Tax</th>
                                <th className="px-3 py-3 text-right">Total Position</th>
                                <th className="px-3 py-3">Status</th>
                                <th className="px-3 py-3">Next Key Date</th>
                                <th className="px-3 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const pill = STATUS_PILL[r.has_unknown && r.status === "current" ? "unknown" : r.status] || STATUS_PILL.unknown;
                                return (
                                    <tr key={r.client_id} className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]"
                                        data-testid={`row-${r.client_id}`}>
                                        <td className="px-3 py-3"><input type="checkbox" className="h-3.5 w-3.5" /></td>
                                        <td className="px-3 py-3">
                                            <div className="font-medium text-[#0F1115]">{r.client_name}</div>
                                            {r.ird_number && <div className="text-[11.5px] text-[#5E636E]">IRD {r.ird_number}</div>}
                                        </td>
                                        <td className="px-3 py-3">
                                            {r.last_ird_check_at ? (
                                                <div>
                                                    <div className="text-[#0F1115]">{new Date(r.last_ird_check_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}</div>
                                                    {r.verified_by_name && <div className="text-[11.5px] text-[#5E636E]">by {r.verified_by_name}</div>}
                                                </div>
                                            ) : <span className="text-[#A1A1AA] italic">Not checked</span>}
                                        </td>
                                        <td className={`px-3 py-3 text-right tabular-nums ${r.overdue_now > 0 ? "text-red-600 font-semibold" : "text-[#5E636E]"}`}>
                                            {nz(r.overdue_now)}
                                        </td>
                                        <td className={`px-3 py-3 text-right tabular-nums ${r.due_soon > 0 ? "text-amber-600 font-semibold" : "text-[#5E636E]"}`}>
                                            {nz(r.due_soon)}
                                        </td>
                                        <td className="px-3 py-3 text-right tabular-nums text-[#0F1115]">{nz(r.future_tax)}</td>
                                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-[#0F1115]">{nz(r.total_position)}</td>
                                        <td className="px-3 py-3">
                                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${pill.cls}`}>
                                                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                                {pill.label}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 text-[12.5px] text-[#0F1115]">{r.next_key_label || "—"}</td>
                                        <td className="px-3 py-3">
                                            <div className="flex items-center gap-1">
                                                <Link to={`/app/tax-position/clients/${r.client_id}`}
                                                    className="rounded-md border border-[#E4E4E7] bg-white px-2.5 py-1 text-[12px] font-medium text-[#0F1115] hover:bg-zinc-50"
                                                    data-testid={`view-${r.client_id}`}>
                                                    View / Update
                                                </Link>
                                                <button className="rounded-md border border-transparent px-1 py-1 text-[#5E636E] hover:bg-zinc-100">
                                                    <MoreVertical className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!filtered.length && (
                                <tr><td colSpan={10} className="py-10 text-center text-[13px] text-[#5E636E]">
                                    {loading ? "Loading…" : "No clients match that filter."}
                                    {!loading && !data.rows.length && (
                                        <div className="mt-3">
                                            <Button onClick={seedDemo} disabled={seeding} data-testid="seed-demo-btn">
                                                {seeding ? "Seeding…" : "Load 6 fictional demo clients"}
                                            </Button>
                                        </div>
                                    )}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
